import React, { useEffect, useMemo, useRef, useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { yup } from '@reapitform/utils'
import { ButtonGroup, Checkbox, Drawer, InputGroup, Select } from '@reapitform/common-ui'
import { Button } from '@reapit/elements/core/button'
import { TextareaControl } from '@reapit/elements/core/textarea-control'
import { toast } from '@reapit/elements/core/toaster'
import { useAdminEnums } from '../../../../api/enums'
import { useAdminMergeFields } from '../../../../api/merge-fields'
import { useSetFieldLink } from '../../../../api/field-links'
import { AdminRecipientField, EnumOption, FormFieldLinkOption } from '../../../../api/types'
import { useCreateAdminRecipientField, useUpdateAdminRecipientField } from '../../../../api/recipient-fields'
import { FieldHelp, FieldStack } from '../../../../components/form-details-fields/style'

// Explicit "None" options — the enum catalog excludes None sentinels, but both are valid, common choices here
// (most fields are plain data, not actions; most have no special formatting).
const NONE_ACTION_TYPE: EnumOption = { value: 'None', label: 'None – plain data field' }
const NONE_FORMATTING_TYPE: EnumOption = { value: 'None', label: 'None' }
// Sentinel for "no linked form field" — an empty referenceID can never collide with a real field.
const NONE_LINKED_FIELD: FormFieldLinkOption = { referenceID: '', label: 'None – no linked field' }

interface RecipientFieldValues {
  actionType: string
  displayType: string
  formattingType: string
  displayIndex: number
  displaySubIndex: number
  displayPrefix: string
  displaySuffix: string
  defaultData: string
  booleanTrueData: string
  booleanFalseData: string
  dataIsHtml: boolean
  defaultDataHasMergeField: boolean
}

const recipientFieldSchema = yup.object({
  actionType: yup.string().optional().default('None'),
  displayType: yup.string().required('Choose where this field displays'),
  formattingType: yup.string().optional().default('None'),
  displayIndex: yup
    .number()
    .typeError('Enter a valid number')
    .min(0, 'Display index must be 0 or more')
    .required('Enter a display index'),
  displaySubIndex: yup.number().typeError('Enter a valid number').min(0, 'Must be 0 or more').default(0),
  displayPrefix: yup.string().optional().default(''),
  displaySuffix: yup.string().optional().default(''),
  defaultData: yup.string().optional().default(''),
  booleanTrueData: yup.string().optional().default(''),
  booleanFalseData: yup.string().optional().default(''),
  dataIsHtml: yup.boolean().default(false),
  defaultDataHasMergeField: yup.boolean().default(false),
})

interface RecipientFieldDrawerProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  recipientCardReferenceID: string
  /** The field being edited, or null to create a new one. */
  field: AdminRecipientField | null
  /** All existing fields on the card, in the same display region — used to prevent duplicate display indices. */
  existingFields: AdminRecipientField[]
  /** Default display index for a new field (appended to the end of its region). */
  nextDisplayIndex: number
  /** Every agent-side form field eligible as a link target, with card/section context in its label. */
  formFieldOptions: FormFieldLinkOption[]
  /** The form field currently linked to this recipient field, if any. */
  linkedFormFieldReferenceID: string | null
  onSaved: () => void
}

/** Add/Edit drawer for a recipient field. Creates (POST) or updates (PUT) then triggers a builder refresh. */
export const RecipientFieldDrawer: React.FC<RecipientFieldDrawerProps> = ({
  isOpen,
  onOpenChange,
  recipientCardReferenceID,
  field,
  existingFields,
  nextDisplayIndex,
  formFieldOptions,
  linkedFormFieldReferenceID,
  onSaved,
}) => {
  const formId = 'recipient-field-form'
  const { data: enums } = useAdminEnums()
  const { data: mergeFields } = useAdminMergeFields()
  const { setRecipientLink } = useSetFieldLink()
  const [createField, creating] = useCreateAdminRecipientField(recipientCardReferenceID)
  const [updateField, updating] = useUpdateAdminRecipientField(recipientCardReferenceID, field?.referenceID ?? '')
  const isBusy = creating || updating

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    watch,
    reset,
    formState: { errors },
  } = useForm<RecipientFieldValues>({
    resolver: yupResolver(recipientFieldSchema),
    defaultValues: {
      actionType: 'None',
      displayType: 'Main',
      formattingType: 'None',
      displayIndex: nextDisplayIndex,
      displaySubIndex: 0,
      displayPrefix: '',
      displaySuffix: '',
      defaultData: '',
      booleanTrueData: '',
      booleanFalseData: '',
      dataIsHtml: false,
      defaultDataHasMergeField: false,
    },
  })

  const [actionTypeOption, setActionTypeOption] = useState<EnumOption>(NONE_ACTION_TYPE)
  const [displayTypeOption, setDisplayTypeOption] = useState<EnumOption | null>(null)
  const [formattingTypeOption, setFormattingTypeOption] = useState<EnumOption>(NONE_FORMATTING_TYPE)
  const [defaultIsMergeField, setDefaultIsMergeField] = useState(false)
  const [selectedFormFieldRef, setSelectedFormFieldRef] = useState<string | null>(null)
  const defaultDataTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  const actionTypeOptions = useMemo<EnumOption[]>(
    () => [NONE_ACTION_TYPE, ...(enums?.RecipientActionType ?? [])],
    [enums],
  )
  const formattingTypeOptions = useMemo<EnumOption[]>(
    () => [NONE_FORMATTING_TYPE, ...(enums?.DisplayFormattingType ?? [])],
    [enums],
  )
  const displayTypeOptions = enums?.RecipientCardSection ?? []
  const linkedFieldOptions = useMemo<FormFieldLinkOption[]>(() => {
    const options: FormFieldLinkOption[] = [NONE_LINKED_FIELD, ...formFieldOptions]
    // If the linked field was deleted (or formFieldOptions is stale from another session), fall back to a
    // placeholder so the Select shows what's actually saved instead of silently defaulting to "None".
    if (linkedFormFieldReferenceID && !options.some((option) => option.referenceID === linkedFormFieldReferenceID)) {
      options.push({ referenceID: linkedFormFieldReferenceID, label: `Missing field (${linkedFormFieldReferenceID})` })
    }
    return options
  }, [formFieldOptions, linkedFormFieldReferenceID])
  const mergeFieldOptions = useMemo<EnumOption[]>(
    () =>
      (mergeFields ?? []).map((mergeField) => ({
        value: mergeField.token,
        label: `${mergeField.category} · ${mergeField.name}`,
      })),
    [mergeFields],
  )

  // Reset the form to the field's values (or blank create defaults) each time the drawer opens.
  useEffect(() => {
    if (!isOpen) return
    reset({
      actionType: field?.actionType || 'None',
      displayType: field?.displayType || 'Main',
      formattingType: field?.formattingType || 'None',
      displayIndex: field?.displayIndex ?? nextDisplayIndex,
      displaySubIndex: field?.displaySubIndex ?? 0,
      displayPrefix: field?.displayPrefix ?? '',
      displaySuffix: field?.displaySuffix ?? '',
      defaultData: field?.defaultData ?? '',
      booleanTrueData: field?.booleanTrueData ?? '',
      booleanFalseData: field?.booleanFalseData ?? '',
      dataIsHtml: field?.dataIsHtml ?? false,
      defaultDataHasMergeField: field?.defaultDataHasMergeField ?? false,
    })
    setDefaultIsMergeField(Boolean(field?.defaultDataHasMergeField))
    setSelectedFormFieldRef(linkedFormFieldReferenceID)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, field, linkedFormFieldReferenceID])

  useEffect(() => {
    if (!isOpen) return
    const actionTypeValue = field?.actionType || 'None'
    setActionTypeOption(actionTypeOptions.find((option) => option.value === actionTypeValue) ?? NONE_ACTION_TYPE)
    const displayTypeValue = field?.displayType || 'Main'
    setDisplayTypeOption(displayTypeOptions.find((option) => option.value === displayTypeValue) ?? null)
    const formattingTypeValue = field?.formattingType || 'None'
    setFormattingTypeOption(
      formattingTypeOptions.find((option) => option.value === formattingTypeValue) ?? NONE_FORMATTING_TYPE,
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, field, enums])

  const handleClose = () => onOpenChange(false)

  const isAction = watch('actionType') !== 'None'
  const isBoolean = watch('formattingType') === 'Boolean'

  const onSubmit: SubmitHandler<RecipientFieldValues> = async (values) => {
    // The (index, sub-index) pair must be unique within the region (excluding the field being edited) — two
    // fields can legitimately share a display index if they sit side by side at different sub-indices.
    const isTaken = existingFields
      .filter((existing) => existing.referenceID !== field?.referenceID)
      .filter((existing) => existing.displayType === values.displayType)
      .some(
        (existing) =>
          existing.displayIndex === values.displayIndex && existing.displaySubIndex === values.displaySubIndex,
      )
    if (isTaken) {
      setError('displaySubIndex', {
        type: 'manual',
        message: `A field already exists at display index ${values.displayIndex}, sub-index ${values.displaySubIndex} in this region. Choose a different sub-index.`,
      })
      return
    }

    const payload = {
      ...values,
      displayPrefix: values.displayPrefix || null,
      displaySuffix: values.displaySuffix || null,
      defaultData: values.defaultData || null,
      booleanTrueData: isBoolean ? values.booleanTrueData || null : null,
      booleanFalseData: isBoolean ? values.booleanFalseData || null : null,
    }

    let savedField: AdminRecipientField
    try {
      savedField = field ? await updateField(payload) : await createField(payload)
    } catch {
      // createField/updateField already surface their own error via the hooks' internal toast — nothing more to do.
      return
    }

    try {
      // Reconcile the form-field link only if it changed. Link the new target BEFORE unlinking the old one —
      // if the link call fails, the old link is untouched and the user is back where they started, rather
      // than left with neither link if we cleared the old one first and the new link then failed.
      if (selectedFormFieldRef !== linkedFormFieldReferenceID) {
        if (selectedFormFieldRef) await setRecipientLink(selectedFormFieldRef, savedField.referenceID)
        if (linkedFormFieldReferenceID) await setRecipientLink(linkedFormFieldReferenceID, null)
      }

      onSaved()
      onOpenChange(false)
    } catch (error) {
      // setRecipientLink is a plain imperative fetch with no toast of its own, unlike createField/updateField above.
      toast.error(error instanceof Error ? error.message : 'Saved the field, but failed to update its link.')
    }
  }

  const footer = (
    <ButtonGroup alignment="right">
      <Button size="medium" variant="secondary" disabled={isBusy} onClick={handleClose} type="button">
        Cancel
      </Button>
      <Button size="medium" variant="primary" isBusy={isBusy} disabled={isBusy} type="submit" form={formId}>
        Save
      </Button>
    </ButtonGroup>
  )

  return (
    <Drawer
      isOpen={isOpen}
      title={field ? 'Edit Recipient Field' : 'Add Recipient Field'}
      onClose={handleClose}
      footerItems={footer}
    >
      {isOpen && (
        <FieldStack id={formId} onSubmit={handleSubmit(onSubmit)}>
          <div>
            <Select
              label="Displays In *"
              options={displayTypeOptions}
              value={displayTypeOption}
              disabled={Boolean(field)}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                setDisplayTypeOption(option)
                setValue('displayType', option?.value ?? '')
              }}
              errorMessage={errors.displayType?.message}
            />
            <FieldHelp>
              Which region of the card this field renders in — Heading, Main, or Additional.
              {field && ' Fixed once created — delete and re-add the field to change it.'}
            </FieldHelp>
          </div>
          <InputGroup
            label="Display Index *"
            labelInformationTooltip="Order within its region — lower shows first. Must be unique among fields in the same region."
            inputMode="numeric"
            {...register('displayIndex', { valueAsNumber: true })}
            errorMessage={errors.displayIndex?.message}
          />
          <InputGroup
            label="Display Sub-Index"
            labelInformationTooltip="Breaks ties on Display Index for fields that share a row (e.g. side-by-side values)."
            inputMode="numeric"
            {...register('displaySubIndex', { valueAsNumber: true })}
            errorMessage={errors.displaySubIndex?.message}
          />
          <div>
            <Select
              label="Action Type"
              options={actionTypeOptions}
              value={actionTypeOption}
              disabled={Boolean(field)}
              getOptionLabel={(option: EnumOption) => option.label}
              getOptionValue={(option: EnumOption) => option.value}
              onChange={(option: EnumOption | null) => {
                const next = option ?? NONE_ACTION_TYPE
                setActionTypeOption(next)
                setValue('actionType', next.value)
              }}
              errorMessage={errors.actionType?.message}
            />
            <FieldHelp>
              Leave as None for a plain data field. Any other type renders as that action (e.g. a signature slot)
              instead of a value.
              {field && ' Fixed once created — delete and re-add the field to change it.'}
            </FieldHelp>
          </div>
          {!isAction && (
            <>
              <div>
                <Select
                  label="Linked Form Field"
                  options={linkedFieldOptions}
                  value={
                    linkedFieldOptions.find((option) => option.referenceID === (selectedFormFieldRef ?? '')) ??
                    NONE_LINKED_FIELD
                  }
                  getOptionLabel={(option: FormFieldLinkOption) => option.label}
                  getOptionValue={(option: FormFieldLinkOption) => option.referenceID}
                  onChange={(option: FormFieldLinkOption | null) =>
                    setSelectedFormFieldRef(option?.referenceID || null)
                  }
                />
                <FieldHelp>
                  Feeds this field from the recipient&apos;s answer to that agent-side field, instead of the default
                  value below. Leave blank to always show the default value.
                </FieldHelp>
              </div>
              <InputGroup
                label="Display Prefix"
                placeholder="e.g. Mobile:"
                {...register('displayPrefix')}
                errorMessage={errors.displayPrefix?.message}
              />
              <InputGroup
                label="Display Suffix"
                {...register('displaySuffix')}
                errorMessage={errors.displaySuffix?.message}
              />
              <TextareaControl
                label="Default Value"
                helpText="The literal value shown when this field isn't linked to a form field. Supports long or HTML content (e.g. standard terms), with merge tokens (e.g. {{RecipientFullName}}) mixed in — place your cursor where you want one and use the picker below to insert it there."
                fieldSizing="fixed"
                rows={6}
                {...(() => {
                  const { ref: rhfRef, ...rest } = register('defaultData')
                  return {
                    ...rest,
                    ref: (element: HTMLTextAreaElement | null) => {
                      rhfRef(element)
                      defaultDataTextareaRef.current = element
                    },
                  }
                })()}
                errorText={errors.defaultData?.message}
              />
              <Checkbox
                label="Default value contains a merge field"
                supplementaryInfo="Turn on if the text above contains any {{token}} — otherwise it's shown as literal text instead of being resolved."
                checked={defaultIsMergeField}
                onChange={(event) => {
                  const checked = (event.target as HTMLInputElement).checked
                  setDefaultIsMergeField(checked)
                  setValue('defaultDataHasMergeField', checked)
                }}
              />
              <Select
                label="Insert a merge field"
                isClearable
                optionLabelKey="label"
                optionValueKey="value"
                options={mergeFieldOptions}
                value={null}
                getOptionLabel={(option: EnumOption) => option.label}
                getOptionValue={(option: EnumOption) => option.value}
                onChange={(option: EnumOption | null) => {
                  if (!option) return
                  const textarea = defaultDataTextareaRef.current
                  const current = watch('defaultData') ?? ''
                  // Insert at the cursor when we have one; otherwise fall back to appending at the end.
                  const start = textarea?.selectionStart ?? current.length
                  const end = textarea?.selectionEnd ?? current.length
                  const next = current.slice(0, start) + option.value + current.slice(end)
                  setValue('defaultData', next)
                  setDefaultIsMergeField(true)
                  setValue('defaultDataHasMergeField', true)

                  // Restore focus and place the cursor right after the inserted token — has to happen after
                  // the textarea's value actually updates, so defer a tick past this render.
                  const cursorPosition = start + option.value.length
                  requestAnimationFrame(() => {
                    textarea?.focus()
                    textarea?.setSelectionRange(cursorPosition, cursorPosition)
                  })
                }}
              />
              <div>
                <Select
                  label="Formatting"
                  options={formattingTypeOptions}
                  value={formattingTypeOption}
                  getOptionLabel={(option: EnumOption) => option.label}
                  getOptionValue={(option: EnumOption) => option.value}
                  onChange={(option: EnumOption | null) => {
                    const next = option ?? NONE_FORMATTING_TYPE
                    setFormattingTypeOption(next)
                    setValue('formattingType', next.value)
                  }}
                  errorMessage={errors.formattingType?.message}
                />
                <FieldHelp>How the value is displayed — e.g. as a boolean, a date, or a currency amount.</FieldHelp>
              </div>
              {isBoolean && (
                <>
                  <InputGroup
                    label="Text When True"
                    placeholder="e.g. Allowed"
                    {...register('booleanTrueData')}
                    errorMessage={errors.booleanTrueData?.message}
                  />
                  <InputGroup
                    label="Text When False"
                    placeholder="e.g. Not Allowed"
                    {...register('booleanFalseData')}
                    errorMessage={errors.booleanFalseData?.message}
                  />
                </>
              )}
              <Checkbox
                label="Value Is HTML"
                supplementaryInfo="Renders the value as HTML (e.g. for standard terms) instead of plain text."
                checked={Boolean(watch('dataIsHtml'))}
                onChange={() => setValue('dataIsHtml', !watch('dataIsHtml'))}
              />
            </>
          )}
        </FieldStack>
      )}
    </Drawer>
  )
}
